import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, ExternalLink, Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { waNumber, renderMsg, waUrl as buildWaUrl, WA_TARGET } from "@/components/crm/WhatsAppQueue";

const VARS = ["[NOME]", "[COGNOME]", "[INTERESSE]", "[COMMERCIALE]"];

// Dialog WhatsApp singolo lead (assistito): apre WhatsApp Web/app col numero + testo precompilato.
// Ciclo: preparato -> aperto -> confermato (manuale, NON verificato) / annullato.
export default function WhatsAppLeadDialog({ open, onClose, lead, userName, onLogged }) {
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [text, setText] = useState("");
  const [mid, setMid] = useState(null);
  const [opened, setOpened] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setTplId(""); setText(""); setOpened(false); setMid(null);
    api.get("/whatsapp-templates", { params: { active_only: true } }).then((r) => setTemplates(r.data)).catch(() => {});
    api.post("/whatsapp/messages", { lead_ids: [lead.id], message: "" })
      .then((r) => setMid(r.data?.[0]?.id || null)).catch(() => {});
  }, [open, lead]);

  if (!lead) return null;
  const num = waNumber(lead.phone);
  const preview = renderMsg(text, lead, userName);
  const waUrl = buildWaUrl(num, preview);

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) setText(t.text);
  };

  const patch = (status) => { if (mid) return api.patch(`/whatsapp/messages/${mid}`, { status }).catch(() => {}); };

  // Apertura tramite vero link <a target>: registra solo "aperto", non l'invio
  const openWa = () => { setTimeout(() => { setOpened(true); patch("opened"); }, 0); };

  const confirmSent = async () => {
    setSaving(true);
    try {
      await patch("confirmed");
      toast.success("Invio confermato manualmente");
      onLogged && onLogged();
      onClose();
    } catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  const cancelAndClose = () => { if (mid && !opened) patch("cancelled"); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancelAndClose(); }}>
      <DialogContent className="max-w-md" data-testid="wa-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-500" /> WhatsApp — {lead.first_name} {lead.last_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {num ? (
            <div className="text-sm text-muted-foreground" data-testid="wa-lead-phone">{lead.phone} <span className="opacity-60">(+{num})</span></div>
          ) : (
            <div className="text-sm text-destructive font-medium" data-testid="wa-lead-invalid">Numero mancante o non valido</div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Template (opzionale)</Label>
            <Select value={tplId} onValueChange={pickTemplate}>
              <SelectTrigger data-testid="wa-lead-template"><SelectValue placeholder="Nessuno (scrivi sotto)" /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Nessun template attivo</div>}
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.interest ? ` · ${t.interest}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Messaggio (opzionale)</Label>
            <Textarea data-testid="wa-lead-text" rows={4} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Ciao [NOME], sono [COMMERCIALE]…" />
            <div className="flex flex-wrap gap-1 pt-1">
              {VARS.map((v) => (
                <button key={v} type="button" onClick={() => setText((x) => x + v)}
                  className="text-[11px] px-2 py-0.5 rounded-full border bg-muted hover:bg-accent">{v}</button>
              ))}
            </div>
          </div>
          {text.trim() && (
            <div className="rounded-md border bg-card p-3 text-sm whitespace-pre-wrap" data-testid="wa-lead-preview">{preview}</div>
          )}
          {opened && waUrl && (
            <a href={waUrl} target={WA_TARGET} data-testid="wa-lead-fallback"
              className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 underline">
              <AlertTriangle className="w-4 h-4" /> La chat non si è aperta? Clicca qui per riaprirla
            </a>
          )}
          <p className="text-xs text-muted-foreground">
            L'invio avviene manualmente da WhatsApp. "Conferma invio" è una conferma manuale, NON una verifica tecnica di consegna.
          </p>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {num ? (
            <Button asChild variant="outline" className="gap-1.5">
              <a href={waUrl} target={WA_TARGET} onClick={openWa} data-testid="wa-lead-open">
                <ExternalLink className="w-4 h-4" /> Apri WhatsApp
              </a>
            </Button>
          ) : (
            <Button variant="outline" className="gap-1.5" disabled data-testid="wa-lead-open">
              <ExternalLink className="w-4 h-4" /> Apri WhatsApp
            </Button>
          )}
          <Button className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={confirmSent} disabled={saving || !opened} data-testid="wa-lead-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Conferma invio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
